import React from 'react'
import axios from 'axios'
import { FETCH_STATUS } from './fetch/constants'
import { getFullUrl } from './utils'

const getDisplayName = WrappedComponent =>
    WrappedComponent.displayName || WrappedComponent.name || 'Component'

const withFetch = (resource, WrappedComponent) => {
    class FetchHOC extends React.Component {
        state = {
            status: FETCH_STATUS.NOT_STARTED,
            data: [],
        }

        componentDidMount() {
            this.fetchData(this.getUrl())
        }

        componentDidUpdate() {
            if (typeof resource === 'function' && this.urlHasChanged()) {
                this.cancellationTokenSource.cancel()
                this.fetchData(this.getUrl())
            }
        }

        componentWillUnmount() {
            this.cancellationTokenSource.cancel()
        }

        getUrl = () => {
            let url = resource
            if (typeof resource === 'function') {
                url = resource(this.props)
            }

            return getFullUrl(url)
        }

        retry = async () => {
            await this.fetchData(this.getUrl())
        }

        cancellationTokenSource = null
        prevUrl = this.getUrl()

        fetchData(url) {
            if (!url) return

            this.cancellationTokenSource = axios.CancelToken.source()

            const setStateToRequested = () =>
                this.setState(prev => ({
                    ...prev,
                    status: FETCH_STATUS.REQUESTED,
                }))

            const setStateToSucceeded = data =>
                this.setState(prev => ({
                    ...prev,
                    data,
                    status: FETCH_STATUS.SUCCEEDED,
                }))

            const setStateToFailed = () =>
                this.setState(prev => ({
                    ...prev,
                    status: FETCH_STATUS.FAILED,
                }))

            Promise.resolve()
                .then(setStateToRequested)
                .then(() =>
                    axios.get(url, {
                        cancelToken: this.cancellationTokenSource.token,
                    }),
                )
                .then(response => setStateToSucceeded(response.data))
                .catch(e => {
                    if (axios.isCancel(e)) return
                    setStateToFailed()
                })
        }

        urlHasChanged() {
            if (typeof resource !== 'function') {
                return this.prevUrl !== resource
            }

            const currentUrl = this.getUrl()
            if (this.prevUrl !== currentUrl) {
                this.prevUrl = currentUrl
                return true
            }

            return false
        }

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    {...this.state}
                    retry={this.retry}
                />
            )
        }
    }

    FetchHOC.displayName = `WithFetch(${getDisplayName(WrappedComponent)})`

    return FetchHOC
}

export default withFetch
